import actions from "./actions";
import {message} from "antd";
import {t} from "@lingui/macro";
import {StorageKey} from "../../enums/StorageKey";
import {readStorage} from "./helpers/readStorage";
import {writeStorage} from "./helpers/writeStorage";
import {ColumnsHelper} from "../../components/Columns/ColumnsHelper";
import {deleteStorage} from "./helpers/deleteStorage";
import _ from "underscore";

// export const CustomizeColumnHelper = {
//     writeToStorage(colKey, value) {
//         const storageKey = CustomizeColumnHelper.getStorageKey(colKey);
//         const initState = getInitState(colKey);
//
//         // ENG-1696 If value same as initState, remove cache
//         if (_.isEqual(value, initState)) {
//             localStorage.removeItem(storageKey);
//             return null;
//         }
//
//         const jsonString = JSON.stringify(value);
//         localStorage.setItem(storageKey, jsonString);
//         return null;
//     },
// };

const isObject = params => (
    typeof params === 'object' &&
    !Array.isArray(params) &&
    params !== null
);

function initState() {
    const cacheVersion = readStorage('cache_version') || null;
    const initState = {
        [StorageKey.cache_collapse_reports]: readStorage(StorageKey.cache_collapse_reports) || [],
        [StorageKey.cache_favourite_reports]: readStorage(StorageKey.cache_favourite_reports) || [],
        [StorageKey.cache_custom_fields]: readStorage(StorageKey.cache_custom_fields) || [],
    };

    // Fields from cache
    const fields = initState[StorageKey.cache_custom_fields];

    // Columns, Page Size, Sorting etc.
    Object.keys(StorageKey).forEach(key => {
        // Skip all "cache_"
        if (key.startsWith('cache_'))
            return null;

        const isInvalid = cacheVersion < 1001;

        function reset() {
            initState[key] = {
                columns: ColumnsHelper.getDefaultColumns(
                    key,
                ), // no fields
                sort_by: null,
                sort_dir: null,
                page_size: null,
            };
            return deleteStorage(key);
        }

        // Case 1: Invalid due to Cache Version
        if (isInvalid) {
            const storageData = readStorage(`cols_${key}`) || null;

            // Case 1a: Migration
            if (isObject(storageData) && !storageData.sort_by) {
                const defaultColumns = Object.keys(storageData).map(key => ({
                    key,
                    width: storageData[key] || null,
                }));
                initState[key] = {
                    columns: defaultColumns, // no fields
                    sort_by: null,
                    sort_dir: null,
                    page_size: null,
                };
                writeStorage(key, initState[key]);

                return deleteStorage(`cols_${key}`);
            }

            // Case 1b: Create New
            return reset();
        }

        // Case 2: If blank
        const storageData = readStorage(key) || {};

        if (Object.keys(storageData).length === 0)
            return reset();

        // Case 3: Invalid or Missing Required Column Keys
        const allColumns = ColumnsHelper.getAllColumns(key, {}, fields);
        const allKeys = allColumns.map(col => col.key);
        const allRequiredKeys = allColumns.filter(col => col.required).map(col => col.key);

        const storedKeys = ColumnsHelper.extractColumnKeys(storageData);
        const invalid = storedKeys.filter(key => !allKeys.includes(key));
        const missingRequired = allRequiredKeys.filter(key => !storedKeys.includes(key));

        if (invalid.length || missingRequired.length)
            return reset();

        // Case 4: Check if sort_by is valid
        if (
            storageData.sort_by &&
            !allColumns.find(col => col.key === storageData.sort_by || col.dataIndex === storageData.sort_by)
        ) {
            storageData.sort_by = null;
            storageData.sort_dir = null;
            writeStorage(key, storageData);
        }

        // Case 5: Load the stored data
        initState[key] = storageData;

        return null;
    });

    return initState;
}

function writeRemoveStorage(key, value) {
    const defaultValue = {
        columns: ColumnsHelper.getDefaultColumns(
            key,
        ), // no fields
        sort_by: null,
        sort_dir: null,
        page_size: null,
    };

    if (!_.isEqual(defaultValue, value))
        writeStorage(key, value);
    else
        deleteStorage(key);
}
export default function appReducer(state = initState(), action = {}) {
    switch (action.type) {
        case actions.LOCAL_STORAGE_SELECT_COLUMNS: {
            const storageKey = action.key;

            const columns = [...(state[storageKey]?.columns || [])];
            const newColumns = action.columnKeys.map(value => {
                const column = columns.find(column => column.key === value);

                if (!column) {
                    return {
                        key: value,
                        width: null,
                    };
                }
                return column;
            });

            const newState = {
                ...state,
                [storageKey]: {
                    ...state[storageKey],
                    columns: newColumns,
                },
            };
            writeRemoveStorage(storageKey, newState[storageKey]);
            return newState;
        }
        case actions.LOCAL_STORAGE_SET_COLUMN_WIDTH: {
            const storageKey = action.key;

            // key, columnKey, width
            const newColumns = [...(state[storageKey]?.columns || [])];

            const columnIdx = newColumns.findIndex(column => column.key === action.columnKey);
            if (columnIdx !== -1)
                newColumns[columnIdx].width = action.width;

            const newState = {
                ...state,
                [storageKey]: {
                    ...state[storageKey],
                    columns: newColumns,
                },
            };

            writeRemoveStorage(storageKey, newState[storageKey]);
            return newState;
        }

        case actions.LOCAL_STORAGE_SET_SORT_PARAMS: {
            const storageKey = action.key;
            const { column, order } = action.sorter;
            const { dataIndex, key } = column || {};

            let sortDir = null;
            let sortBy = null;
            switch (order) {
                case 'ascend':
                case 'asc':
                    sortDir = 'asc';
                    sortBy = dataIndex || key;
                    break;
                case 'descend':
                    sortDir = 'desc';
                    sortBy = dataIndex || key;
                    break;
                default:
                    break;
            }

            const newState = {
                ...state,
                [storageKey]: {
                    ...state[storageKey],
                    sort_by: sortBy,
                    sort_dir: sortDir,
                },
            };
            writeRemoveStorage(storageKey, newState[storageKey]);
            return newState;
        }
        case actions.LOCAL_STORAGE_SET_PAGE_SIZE: {
            const storageKey = action.key;

            const newState = {
                ...state,
                [storageKey]: {
                    ...state[storageKey],
                    page_size: action.pageSize,
                },
            };
            writeRemoveStorage(storageKey, newState[storageKey]);
            return newState;
        }
        case actions.LOCAL_STORAGE_SET_REPORT_LABEL: {
            const storageKey = action.key;

            const newState = {
                ...state,
                [storageKey]: {
                    ...state[storageKey],
                    report_label: action.reportLabel,
                },
            };
            writeRemoveStorage(storageKey, newState[storageKey]);
            return newState;
        }
        case actions.LOCAL_STORAGE_SET_REPORT_REMARKS: {
            const storageKey = action.key;

            const newState = {
                ...state,
                [storageKey]: {
                    ...state[storageKey],
                    report_remarks: action.reportRemarks,
                },
            };
            writeRemoveStorage(storageKey, newState[storageKey]);
            return newState;
        }
        case actions.LOCAL_STORAGE_TOGGLE_STAR_REPORT: {
            const storageKey = StorageKey.cache_favourite_reports;
            const favouriteReports = [...state[storageKey]];

            const idx = favouriteReports.findIndex(item => item === action.report);

            // Add
            if (idx < 0) {
                favouriteReports.push(action.report);
                message.success(t`Added to Sidebar`);
            }
            // Remove
            else {
                favouriteReports.splice(idx, 1);
                message.success(t`Removed from Sidebar`);
            }

            writeStorage(storageKey, favouriteReports);
            return {
                ...state,
                [storageKey]: favouriteReports,
            };
        }
        case actions.LOCAL_STORAGE_SET_COLLAPSE_REPORT: {
            const storageKey = StorageKey.cache_collapse_reports;
            const collapseReports = [...state[storageKey]];

            const idx = collapseReports.findIndex(item => item === action.item);

            // Add
            if (idx < 0)
                collapseReports.push(action.item);
            // Remove
            else
                collapseReports.splice(idx, 1);

            writeStorage(storageKey, collapseReports);
            return {
                ...state,
                [storageKey]: collapseReports,
            };
        }
        default:
            return state;
    }
}
